// evo/rooms/EvoRoomCard.styled.ts
import styled from 'styled-components';
import Link from 'next/link';
import { Button } from '@lmn/design-system';
import customCss from '@/app/components/hotel-detail/css/customCss';

// Card: full-bleed image on top (mobile, flex column) → side 200px image (desktop, CSS grid).
// On desktop the divider and price row span both columns; `LowerSection` uses `display: contents`
// so its children participate directly in the card grid.
export const RoomCardContainer = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected
        ? theme.kallisto.color.border.interactive.borderPrimaryDefault
        : theme.kallisto.color.border.static.borderNeutral};
  outline: ${({ $selected, theme }) =>
    $selected ? `1px solid ${theme.kallisto.color.border.interactive.borderPrimaryDefault}` : 'none'};

  @media ${customCss.breakpoints.sm} {
    display: grid;
    grid-template-columns: 200px 1fr;
    column-gap: ${({ theme }) => theme.kallisto.space.space300};
    row-gap: ${({ theme }) => theme.kallisto.space.space200};
    padding: ${({ theme }) => theme.kallisto.space.space300};
    overflow: visible;
  }
`;

// ---- Mobile gallery (full-bleed, top) ---------------------------------------
export const MobileGallery = styled.div<{ $clickable: boolean }>`
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  @media ${customCss.breakpoints.sm} {
    display: none;
  }
`;

export const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2px;
  width: 100%;
  height: 100%;

  & > *:first-child {
    grid-row: 1 / 3;
  }

  img {
    width: 100%;
    height: 100%;
    min-height: 0;
    object-fit: cover;
    display: block;
  }
`;

export const GallerySingle = styled.div`
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// MPH-935: stands in for a room photo on a Top Secret stay, where the response carries none by
// design. A tinted panel with the Top Secret mark centred on it, rather than the generic
// "no photo available" artwork, which would read as content that failed to load.
export const GalleryTopSecret = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.commons.colors.tshLight};

  /* && beats the width/height the card's own image wrappers set on any img inside them — the mark is
     a fixed-size icon on the panel, not a photo that fills it. */
  && img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`;

export const GalleryCounter = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.kallisto.space.space200};
  right: ${({ theme }) => theme.kallisto.space.space200};
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.kallisto.space.space50} ${theme.kallisto.space.space100}`};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralContrast};
`;

// ---- Desktop image (side, 200x200) ------------------------------------------
export const DesktopRoomImage = styled.div<{ $clickable: boolean }>`
  display: none;

  @media ${customCss.breakpoints.sm} {
    display: block;
    /* Anchors the "no images" caption over the placeholder (MPH-1321). */
    position: relative;
    grid-column: 1;
    grid-row: 1;
    width: 200px;
    height: 200px;
    border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
    overflow: hidden;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

// ---- Lower section (content + divider + price) ------------------------------
// Mobile: padded flex column. Desktop: dissolves into the card grid.
export const LowerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space200};
  padding: ${({ theme }) =>
    `${theme.kallisto.space.space150} ${theme.kallisto.space.space200} ${theme.kallisto.space.space200}`};

  @media ${customCss.breakpoints.sm} {
    display: contents;
  }
`;

export const RoomContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  gap: ${({ theme }) => theme.kallisto.space.space100};

  @media ${customCss.breakpoints.sm} {
    grid-column: 2;
    grid-row: 1;
  }
`;

export const NameRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  align-self: stretch;
`;

export const NameLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

export const BadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space50};
`;

export const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.kallisto.space.space200};
`;

// MPH-1322 (cux_4123 variant T): an 8px gap between the amenities line and "Room details".
export const AmenitiesBlock = styled.div<{ $ctaChanges?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  ${({ $ctaChanges, theme }) => ($ctaChanges ? `gap: ${theme.kallisto.space.space100};` : '')}
`;

export const AmenitiesLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: ${({ theme }) => theme.kallisto.space.space50};
`;

export const Essentials = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.kallisto.space.space50};
`;

// Generic icon + text row used by the meal-plan and cancellation lines.
// `align-items: flex-start` keeps the leading shield icon pinned to the first
// line when the label wraps onto two lines.
export const EssentialRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.kallisto.space.space100};
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};

  & > img,
  & > svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

// Wraps the cancellation label so the info button flows inline right after the
// last word — including when the label wraps onto a second line.
export const CancellationLabel = styled.div`
  min-width: 0;
`;

// The cancellation / promotion label doubles as the trigger that opens its info modal, so the whole
// label + trailing info icon is one inline, text-styled button — tapping the text or the icon opens it.
export const InfoLabelButton = styled.button`
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

export const RoomDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};

  @media ${customCss.breakpoints.sm} {
    grid-column: 1 / -1;
  }
`;

// ---- Price + CTA ------------------------------------------------------------
// MPH-1322 (cux_4123 variant T): below sm the price stacks above a full-width CTA; from sm up the
// row layout is restored, so tablet and desktop keep today's price position.
export const PriceRow = styled.div<{ $ctaChanges?: boolean }>`
  display: flex;
  flex-direction: ${({ $ctaChanges }) => ($ctaChanges ? 'column' : 'row')};
  justify-content: flex-end;
  align-items: ${({ $ctaChanges }) => ($ctaChanges ? 'stretch' : 'center')};
  gap: ${({ $ctaChanges, theme }) => ($ctaChanges ? theme.kallisto.space.space150 : theme.kallisto.space.space300)};

  @media ${customCss.breakpoints.sm} {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.kallisto.space.space300};
  }
`;

// Desktop price: "+ £75" (Title4) followed by "pp" (BodySmall), baseline aligned.
// Sits right next to the CTA (PriceRow is flex-end with a space300 gap), per design.
export const DesktopPrice = styled.div`
  display: none;

  @media ${customCss.breakpoints.sm} {
    display: flex;
    align-items: baseline;
    /* Figma uses space25 (2px); the kallisto space scale jumps 0 → 4px, so hardcode it. */
    gap: 2px;
  }
`;

// Mobile price: "+ £175" (Title3) + "pp" (BodySmall) on one baseline-aligned line, over the
// taxes-included subline. MPH-1322 (cux_4123 variant T): right-aligned above the full-width CTA.
export const MobilePrice = styled.div<{ $ctaChanges?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  ${({ $ctaChanges }) => ($ctaChanges ? 'align-items: flex-end;' : '')}

  @media ${customCss.breakpoints.sm} {
    display: none;
  }
`;

// The amount + "pp" line: large amount with a smaller "pp" suffix, baseline aligned (mirrors DesktopPrice).
export const MobilePriceLine = styled.div<{ $ctaChanges?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  ${({ $ctaChanges }) => ($ctaChanges ? 'justify-content: flex-end;' : '')}
`;

// The CTA switches at lg — not sm like the rest of the card's Desktop*/Mobile* pieces — because
// "Select room" only makes sense where the trip-overview sidebar provides the Continue button, and
// that sidebar exists on lg+ only (EvoDetailsLayout gates it with useIsLgOrGreater, the same
// breakpoints.lg query). Between sm and lg the card keeps the desktop layout but needs the standalone
// "Continue" CTA, or the user has no way to advance to flights (MPH-685).
// EvoRoomCard.test.tsx pins both wrappers to breakpoints.lg.
export const LgUpOnly = styled.div`
  display: none;

  @media ${customCss.breakpoints.lg} {
    display: contents;
  }
`;

export const BelowLgOnly = styled.div`
  display: contents;

  @media ${customCss.breakpoints.lg} {
    display: none;
  }
`;

// The below-lg CTA. MPH-1322 (cux_4123 variant T): full width below sm, where it sits under the price;
// from sm up it keeps today's size next to the price.
export const BelowLgCta = styled(Button)<{ $ctaChanges?: boolean }>`
  ${({ $ctaChanges }) =>
    $ctaChanges
      ? `
    && {
      width: 100%;
    }

    @media ${customCss.breakpoints.sm} {
      && {
        width: auto;
      }
    }
  `
      : ''}
`;

// Selected CTA: light-pink fill with the primary check + label. Static — no hover/pressed change.
export const SelectedButton = styled(Button)`
  && {
    cursor: default;
    background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
  }

  &&:hover:not(:disabled),
  &&:active:not(:disabled) {
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
    background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
  }
`;

export const MealPlanTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space100};
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};
`;

// Debug-only footer line: spans the full card width; DebugInfo right-aligns its content.
export const DebugInfoLink = styled(Link)`
  padding: 4px 8px;
  width: 100%;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};

  @media ${customCss.breakpoints.sm} {
    padding: 0;
    grid-column: 1 / -1;
  }
`;
